import { getApp } from "firebase/app";
import {
  EmailAuthProvider,
  browserSessionPersistence,
  checkActionCode as checkActionCodeFB,
  confirmPasswordReset as confirmPasswordResetFB,
  getAuth,
  reauthenticateWithCredential,
  setPersistence,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFB,
  signOut as signOutFB,
  updatePassword as updatePasswordFB,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

export class FirebaseAuthService {
  static async signInWithEmailAndPassword(email: string, password: string) {
    const app = getApp();
    const auth = getAuth(app);
    await setPersistence(auth, browserSessionPersistence);

    const userCredential = await signInWithEmailAndPasswordFB(auth, email, password);

    return userCredential.user?.uid;
  }

  static async signOut() {
    const app = getApp();
    const auth = getAuth(app);
    await signOutFB(auth);
    document.cookie = "__session=;max-age=0;path=/";
  }

  static async sendPasswordResetEmail(email: string, clientId?: string): Promise<{ status: string; message: string }> {
    const app = getApp();
    const functions = getFunctions(app);
    const cloudFunction = httpsCallable<{ email: string; clientId?: string }, { status: string; message: string }>(
      functions,
      "clientPortal-resetPassword",
    );
    const res = await cloudFunction({ email, clientId });
    return res.data;
  }

  static async confirmPasswordReset(code: string, password: string) {
    const app = getApp();
    const auth = getAuth(app);
    return await confirmPasswordResetFB(auth, code, password);
  }

  static async checkActionCode(code: string) {
    const app = getApp();
    const auth = getAuth(app);
    return await checkActionCodeFB(auth, code);
  }

  static async updatePassword(oldPassword: string, newPassword: string) {
    const app = getApp();
    const auth = getAuth(app);
    const currentUser = auth.currentUser;

    const credential = EmailAuthProvider.credential(currentUser?.email || "", oldPassword);

    if (currentUser) {
      await reauthenticateWithCredential(currentUser, credential);
      return await updatePasswordFB(currentUser, newPassword);
    }
  }

  /**
   * Returns a user token for later authentication
   *
   * @param forceRefresh - forses generation of a fresh token
   * @returns
   */
  static async getUserToken(forceRefresh?: boolean) {
    const app = getApp();
    const auth = getAuth(app);

    return auth.currentUser?.getIdToken(forceRefresh);
  }
}

const findSubarrayIndex = (source: Uint8Array, needle: number[], fromIndex = 0) => {
  if (!needle.length || source.length < needle.length) {
    return -1;
  }

  for (let i = fromIndex; i <= source.length - needle.length; i++) {
    let matches = true;
    for (let j = 0; j < needle.length; j++) {
      if (source[i + j] !== needle[j]) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return i;
    }
  }

  return -1;
};

const parseFilenameFromContentDisposition = (contentDisposition: string) => {
  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FileNameMatch?.[1]) {
    return decodeURIComponent(utf8FileNameMatch[1]);
  }

  const asciiFileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiFileNameMatch?.[1] || "";
};

const extractMultipartFile = (responseBytes: Uint8Array) => {
  const CRLF = [13, 10];
  const CRLFCRLF = [13, 10, 13, 10];

  const boundaryLineEnd = findSubarrayIndex(responseBytes, CRLF);
  if (boundaryLineEnd < 0) {
    return null;
  }

  const boundaryLine = responseBytes.slice(0, boundaryLineEnd);
  const headersEndIndex = findSubarrayIndex(responseBytes, CRLFCRLF, boundaryLineEnd + 2);
  if (headersEndIndex < 0) {
    return null;
  }

  const partHeadersBytes = responseBytes.slice(boundaryLineEnd + 2, headersEndIndex);
  const partHeaders = new TextDecoder().decode(partHeadersBytes);
  const partContentTypeMatch = partHeaders.match(/content-type:\s*([^\r\n]+)/i);
  const partFileNameMatch = partHeaders.match(/filename="?([^"\r\n;]+)"?/i);

  const contentStart = headersEndIndex + 4;
  const multipartEndMarker = [...CRLF, ...Array.from(boundaryLine), 45, 45];
  const multipartBoundaryMarker = [...CRLF, ...Array.from(boundaryLine)];

  let contentEnd = findSubarrayIndex(responseBytes, multipartEndMarker, contentStart);
  if (contentEnd < 0) {
    contentEnd = findSubarrayIndex(responseBytes, multipartBoundaryMarker, contentStart);
  }
  if (contentEnd < 0 || contentEnd <= contentStart) {
    return null;
  }

  return {
    fileBytes: responseBytes.slice(contentStart, contentEnd),
    fileName: partFileNameMatch?.[1] || "",
    contentType: partContentTypeMatch?.[1]?.trim() || "",
  };
};

const triggerDownloadFromBytes = ({
  fileBytes,
  contentType,
  fileName,
}: {
  fileBytes: Uint8Array;
  contentType: string;
  fileName: string;
}) => {
  const fileArrayBuffer = new ArrayBuffer(fileBytes.byteLength);
  new Uint8Array(fileArrayBuffer).set(fileBytes);

  const blob = new Blob([fileArrayBuffer], {
    type: contentType || "application/octet-stream",
  });

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName || "downloaded-file";
  link.click();
  URL.revokeObjectURL(blobUrl);
};

const setSessionCookieFromUserToken = async (forceRefresh = false) => {
  const userToken = await FirebaseAuthService.getUserToken(forceRefresh);

  if (!userToken) {
    document.cookie = "__session=;max-age=0;path=/";
    throw new Error("Missing auth token");
  }

  // Ensure cookie is available to all routes, including download endpoints.
  document.cookie = "__session=" + userToken + ";max-age=3000;path=/";

  return userToken;
};

const handleExpiredSession = () => {
  document.cookie = "__session=;max-age=0;path=/";
  window.alert("Your session has expired. Please log in again.");

  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

/**
 * Open a new window with a short-lived session cookie set so that the backend firebase function can verify that the
 * user is authenticated
 *
 * @param url - see https://developer.mozilla.org/en-US/docs/Web/API/Window/open#parameters
 * @param target - see https://developer.mozilla.org/en-US/docs/Web/API/Window/open#parameters
 * @param features see https://developer.mozilla.org/en-US/docs/Web/API/Window/open#parameters
 */
export const openAuthenticatedWindow = async (url?: string | URL, target?: string, features?: string) => {
  const userToken = await setSessionCookieFromUserToken(true);

  console.log("[openAuthenticatedWindow] Opening URL", {
    url: typeof url === "string" ? url : url?.toString(),
    hasUserToken: Boolean(userToken),
  });

  const normalizedUrl = typeof url === "string" ? url : url?.toString() || "";

  if (normalizedUrl.startsWith("/files/")) {
    try {
      let debugResponse = await fetch(normalizedUrl, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });

      // Retry once with a fresh token if backend reports unauthorized.
      if (debugResponse.status === 401 || debugResponse.status === 403) {
        await setSessionCookieFromUserToken(true);
        debugResponse = await fetch(normalizedUrl, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (debugResponse.status === 401 || debugResponse.status === 403) {
          handleExpiredSession();
          return;
        }
      }

      const contentType = debugResponse.headers.get("content-type") || "";
      const contentDisposition = debugResponse.headers.get("content-disposition") || "";
      const responseBuffer = await debugResponse.arrayBuffer();
      const bytes = new Uint8Array(responseBuffer.slice(0, 16));
      const hexSignature = Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");

      const previewText = Array.from(new Uint8Array(responseBuffer.slice(0, 120)))
        .map((b) => (b >= 32 && b <= 126 ? String.fromCharCode(b) : ""))
        .join("")
        .trim();

      const responseBytes = new Uint8Array(responseBuffer);
      const multipartFile = extractMultipartFile(responseBytes);
      const responseFileName = parseFilenameFromContentDisposition(contentDisposition);
      const urlFileName = decodeURIComponent(normalizedUrl.split("/").pop() || "downloaded-file");

      console.log("[openAuthenticatedWindow][/files probe] Response details", {
        url: normalizedUrl,
        status: debugResponse.status,
        ok: debugResponse.ok,
        contentType,
        contentDisposition,
        byteLength: responseBuffer.byteLength,
        hexSignature,
        previewText,
        multipartDetected: Boolean(multipartFile),
      });

      if (multipartFile) {
        const finalFileName = multipartFile.fileName || responseFileName || urlFileName;
        const finalContentType = multipartFile.contentType || contentType;

        console.log("[openAuthenticatedWindow][/files probe] Extracted multipart file payload", {
          fileName: finalFileName,
          contentType: finalContentType,
          extractedByteLength: multipartFile.fileBytes.byteLength,
        });

        triggerDownloadFromBytes({
          fileBytes: multipartFile.fileBytes,
          contentType: finalContentType,
          fileName: finalFileName,
        });

        return;
      }
    } catch (probeError) {
      console.error("[openAuthenticatedWindow][/files probe] Failed to inspect response", probeError);
    }
  }

  window.open(url, target, features);
};
